import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { AppLayout } from '../components/AppLayout';
import { BookingsCalendar } from '../components/BookingsCalendar';
import { EditBookingDialog } from '../components/EditBookingDialog';
import { isSuperAdmin, logoutAdmin } from '../services/adminAuth';
import {
  deleteBooking,
  getAllBookings,
  pollBookings,
  updateBookingStatus,
} from '../services/bookings';
import { notifyBookingStatusToRequester } from '../services/notifyBooking';
import { Booking, BookingStatus } from '../types/booking';
import {
  formatBookingRange,
  formatOverlapPeriod,
  statusLabel,
} from '../utils/slots';
import { colors } from '../theme';

type FilterTab = 'pending' | 'approved' | 'rejected' | 'all';

type ApproveConflict = {
  pending: Booking;
  approved: Booking[];
};

type ToastState = {
  severity: 'success' | 'error' | 'info';
  message: string;
} | null;

const statusColor: Record<
  BookingStatus,
  'warning' | 'success' | 'error' | 'default'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

function statusActionMessage(
  booking: Booking,
  status: BookingStatus,
  force?: boolean
): string {
  const group = booking.gruppoScout?.trim() || 'Prenotazione';
  if (status === 'approved') {
    return force
      ? `${group}: approvata comunque.`
      : `${group}: approvata.`;
  }
  if (status === 'rejected') {
    return booking.status === 'approved'
      ? `${group}: approvazione annullata.`
      : `${group}: rifiutata.`;
  }
  return `${group}: rimessa in attesa.`;
}

export function AdminDashboardPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<FilterTab>('pending');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [editing, setEditing] = useState<Booking | null>(null);
  const [approveConflict, setApproveConflict] =
    useState<ApproveConflict | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Booking | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const superAdmin = isSuperAdmin();

  useEffect(() => {
    return pollBookings(
      (data) => {
        setBookings(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || 'Errore nel caricamento delle prenotazioni.');
        setLoading(false);
      }
    );
  }, []);

  const filtered = useMemo(() => {
    if (tab === 'all') return bookings;
    return bookings.filter((b) => b.status === tab);
  }, [bookings, tab]);

  const counts = useMemo(
    () => ({
      pending: bookings.filter((b) => b.status === 'pending').length,
      approved: bookings.filter((b) => b.status === 'approved').length,
      rejected: bookings.filter((b) => b.status === 'rejected').length,
      all: bookings.length,
    }),
    [bookings]
  );

  const applyStatusLocally = (id: string, status: BookingStatus) => {
    setBookings((prev) =>
      prev.map((b) =>
        b.id === id ? { ...b, status, updatedAt: Date.now() } : b
      )
    );
  };

  const handleStatus = async (
    booking: Booking,
    status: BookingStatus,
    options?: { force?: boolean }
  ) => {
    setBusyId(booking.id);
    setError(null);
    try {
      const result = await updateBookingStatus(
        booking.id,
        status,
        booking,
        options
      );
      if (result.conflicts && result.conflicts.length > 0) {
        setApproveConflict({
          pending: booking,
          approved: result.conflicts,
        });
        return;
      }
      setApproveConflict(null);
      applyStatusLocally(booking.id, status);
      if (status === 'approved' || status === 'rejected') {
        void notifyBookingStatusToRequester({ ...booking, status }, status);
      }
      setToast({
        severity: 'success',
        message: statusActionMessage(booking, status, options?.force),
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Impossibile aggiornare lo stato della prenotazione.';
      setError(message);
      setToast({ severity: 'error', message });
    } finally {
      setBusyId(null);
    }
  };

  const handleForceApprove = async () => {
    if (!approveConflict) return;
    await handleStatus(approveConflict.pending, 'approved', { force: true });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !isSuperAdmin()) return;
    const target = deleteTarget;
    setBusyId(target.id);
    setError(null);
    try {
      await deleteBooking(target);
      setBookings((prev) => prev.filter((b) => b.id !== target.id));
      setDeleteTarget(null);
      setToast({
        severity: 'success',
        message: `${target.gruppoScout}: richiesta eliminata.`,
      });
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Impossibile eliminare la prenotazione.';
      setError(message);
      setToast({ severity: 'error', message });
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login', { replace: true });
  };

  const refreshBookings = async () => {
    try {
      const data = await getAllBookings();
      setBookings(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Errore nel ricaricamento delle prenotazioni.'
      );
    }
  };

  return (
    <AppLayout
      title="Dashboard prenotazioni"
      hideAdmin
      maxWidth="lg"
      leading={
        <IconButton
          color="inherit"
          edge="start"
          aria-label="Torna al calendario"
          onClick={() => navigate('/')}
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>
      }
      actions={
        <Button
          color="inherit"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          size="small"
        >
          Esci
        </Button>
      }
      floating={
        !calendarOpen ? (
          <Fab
            variant="extended"
            color="primary"
            aria-label="Apri calendario"
            onClick={() => setCalendarOpen(true)}
            sx={{
              position: 'fixed',
              right: { xs: 16, sm: 28 },
              bottom: { xs: 20, sm: 28 },
              zIndex: (t) => t.zIndex.speedDial,
              px: 2.75,
              gap: 0.75,
              boxShadow: `0 4px 16px ${colors.primary}55`,
              '&:hover': {
                boxShadow: `0 6px 20px ${colors.primary}66`,
              },
            }}
          >
            <CalendarMonthOutlinedIcon />
            Calendario
          </Fab>
        ) : null
      }
    >
      <Stack spacing={2.5} sx={{ pb: 12 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.primary,
              fontSize: { xs: '1.6rem', sm: '2rem' },
            }}
          >
            Gestione richieste
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Approva, rifiuta o modifica le prenotazioni della sede.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Tabs
          value={tab}
          onChange={(_, v: FilterTab) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: colors.surfaceContainer,
            borderRadius: 4,
            px: 1,
            border: `1px solid ${colors.outlineVariant}`,
            '& .MuiTab-root': { minHeight: 48, fontWeight: 600 },
          }}
        >
          <Tab label={`In attesa (${counts.pending})`} value="pending" />
          <Tab label={`Approvate (${counts.approved})`} value="approved" />
          <Tab label={`Rifiutate (${counts.rejected})`} value="rejected" />
          <Tab label={`Tutte (${counts.all})`} value="all" />
        </Tabs>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent sx={{ py: 5, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Nessuna prenotazione in questa sezione.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={2}>
            {filtered.map((booking) => (
              <Card key={booking.id}>
                <CardContent sx={{ pb: 1.5 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      mb: 1.5,
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {booking.gruppoScout}
                        {booking.branca ? ` · ${booking.branca}` : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatBookingRange(booking)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={statusLabel(booking.status)}
                      color={statusColor[booking.status]}
                    />
                  </Stack>

                  <Stack spacing={0.75}>
                    <Typography variant="body2">
                      <strong>Referente:</strong> {booking.referenteNome}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Telefono:</strong>{' '}
                      <Box
                        component="a"
                        href={`tel:${booking.telefono}`}
                        sx={{ color: colors.primary, textDecoration: 'none' }}
                      >
                        {booking.telefono}
                      </Box>
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong>{' '}
                      {booking.email ? (
                        <Box
                          component="a"
                          href={`mailto:${booking.email}`}
                          sx={{ color: colors.primary, textDecoration: 'none' }}
                        >
                          {booking.email}
                        </Box>
                      ) : (
                        '—'
                      )}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Branca:</strong> {booking.branca || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Ragazzi previsti:</strong> {booking.numeroRagazzi}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Adulti previsti:</strong>{' '}
                      {booking.numeroAdulti ?? 0}
                    </Typography>
                    {booking.altreInfo && (
                      <Typography variant="body2">
                        <strong>Note:</strong> {booking.altreInfo}
                      </Typography>
                    )}
                  </Stack>
                </CardContent>

                {booking.status === 'pending' && (
                  <CardActions
                    disableSpacing
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0,
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CheckCircleOutlinedIcon />}
                      disabled={busyId === booking.id}
                      onClick={() => handleStatus(booking, 'approved')}
                    >
                      Approva
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<HighlightOffOutlinedIcon />}
                      disabled={busyId === booking.id}
                      onClick={() => handleStatus(booking, 'rejected')}
                    >
                      Rifiuta
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      disabled={busyId === booking.id}
                      onClick={() => setEditing(booking)}
                    >
                      Modifica
                    </Button>
                    {superAdmin && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlinedIcon />}
                        disabled={busyId === booking.id}
                        onClick={() => setDeleteTarget(booking)}
                      >
                        Elimina
                      </Button>
                    )}
                  </CardActions>
                )}

                {booking.status === 'approved' && (
                  <CardActions
                    disableSpacing
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0,
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlinedIcon />}
                      disabled={busyId === booking.id}
                      onClick={() => setEditing(booking)}
                    >
                      Modifica
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      disabled={busyId === booking.id}
                      onClick={() => handleStatus(booking, 'rejected')}
                    >
                      Annulla approvazione
                    </Button>
                    {superAdmin && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlinedIcon />}
                        disabled={busyId === booking.id}
                        onClick={() => setDeleteTarget(booking)}
                      >
                        Elimina
                      </Button>
                    )}
                  </CardActions>
                )}

                {booking.status === 'rejected' && (
                  <CardActions
                    disableSpacing
                    sx={{
                      px: 3,
                      pb: 3,
                      pt: 0,
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Button
                      size="small"
                      disabled={busyId === booking.id}
                      onClick={() => handleStatus(booking, 'pending')}
                    >
                      Rimetti in attesa
                    </Button>
                    {superAdmin && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlinedIcon />}
                        disabled={busyId === booking.id}
                        onClick={() => setDeleteTarget(booking)}
                      >
                        Elimina
                      </Button>
                    )}
                  </CardActions>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        fullScreen={isMobile}
        fullWidth={!isMobile}
        maxWidth="md"
        scroll="paper"
        sx={{ zIndex: (t) => t.zIndex.modal }}
        slotProps={{
          paper: {
            sx: isMobile
              ? {
                  m: 0,
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  maxHeight: '100%',
                  borderRadius: 0,
                  border: 'none',
                }
              : {
                  m: 3,
                  width: '100%',
                  maxWidth: 720,
                  maxHeight: '85vh',
                  borderRadius: '16px',
                  border: `1px solid ${colors.outlineVariant}`,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            fontWeight: 700,
            color: colors.primary,
            px: { xs: 2, sm: 3 },
            pr: 1,
            pt: 2,
            pb: 1,
            flexShrink: 0,
          }}
        >
          Calendario prenotazioni
          <IconButton
            aria-label="Chiudi"
            onClick={() => setCalendarOpen(false)}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers={!isMobile}
          sx={{
            px: { xs: 0, sm: 2.5 },
            pb: { xs: 2, sm: 2.5 },
            pt: { xs: 1, sm: 2 },
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {calendarOpen && <BookingsCalendar compact isAdmin />}
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(approveConflict)}
        onClose={() => setApproveConflict(null)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: '16px',
              border: `1px solid ${colors.outlineVariant}`,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: colors.primary, pr: 6 }}>
          Sovrapposizione in approvazione
          <IconButton
            aria-label="Chiudi"
            onClick={() => setApproveConflict(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary', mb: 2 }}>
            {approveConflict && approveConflict.approved.length > 1
              ? `Questa richiesta si sovrappone a ${approveConflict.approved.length} prenotazioni già approvate.`
              : 'Questa richiesta si sovrappone a una prenotazione già approvata.'}{' '}
            Puoi annullare oppure approvare comunque.
          </DialogContentText>

          {approveConflict && (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: colors.surfaceContainerLow,
                  border: `1px solid ${colors.outlineVariant}`,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', mb: 0.5 }}
                >
                  <Typography sx={{ fontWeight: 700, flex: 1 }}>
                    {approveConflict.pending.gruppoScout}
                  </Typography>
                  <Chip size="small" label="Da approvare" color="warning" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {formatBookingRange(approveConflict.pending)}
                </Typography>
              </Box>

              {approveConflict.approved.map((approved) => (
                <Box
                  key={approved.id}
                  sx={{
                    p: 1.5,
                    borderRadius: '14px',
                    bgcolor: colors.surfaceContainerLow,
                    border: `1px solid ${colors.outlineVariant}`,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', mb: 0.5 }}
                  >
                    <Typography sx={{ fontWeight: 700, flex: 1 }}>
                      {approved.gruppoScout}
                    </Typography>
                    <Chip size="small" label="Già approvata" color="success" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {formatBookingRange(approved)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      fontWeight: 700,
                      color: colors.primary,
                    }}
                  >
                    Conflitto:{' '}
                    {formatOverlapPeriod(approveConflict.pending, approved)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            gap: 1,
            alignItems: 'stretch',
            '& > :not(style)': {
              m: 0,
              alignSelf: 'stretch',
            },
          }}
        >
          <Button
            onClick={() => setApproveConflict(null)}
            variant="outlined"
            disabled={busyId === approveConflict?.pending.id}
            sx={{ height: 'auto', minHeight: 40 }}
          >
            Annulla
          </Button>
          <Button
            onClick={handleForceApprove}
            variant="contained"
            color="warning"
            disabled={busyId === approveConflict?.pending.id}
            startIcon={
              busyId === approveConflict?.pending.id ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircleOutlinedIcon />
              )
            }
            sx={{ height: 'auto', minHeight: 40, whiteSpace: 'normal' }}
          >
            Approva comunque
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (busyId) return;
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Eliminare la richiesta?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `Stai per eliminare definitivamente la prenotazione di ${deleteTarget.gruppoScout} (${formatBookingRange(deleteTarget)}). L’operazione non si può annullare.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={busyId === deleteTarget?.id}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={
              busyId === deleteTarget?.id ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteOutlinedIcon />
              )
            }
            disabled={busyId === deleteTarget?.id}
            onClick={() => void handleConfirmDelete()}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      <EditBookingDialog
        booking={editing}
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        onSaved={() => {
          refreshBookings();
          setToast({
            severity: 'success',
            message: editing?.gruppoScout?.trim()
              ? `${editing.gruppoScout.trim()}: prenotazione aggiornata.`
              : 'Prenotazione aggiornata.',
          });
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setToast(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{
          bottom: { xs: 20, sm: 28 },
          zIndex: (t) => t.zIndex.speedDial + 2,
        }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
            icon={
              toast.severity === 'success' ? (
                <CheckCircleOutlinedIcon />
              ) : undefined
            }
            sx={{
              width: '100%',
              borderRadius: '16px',
              alignItems: 'center',
              boxShadow: `0 8px 24px ${colors.primary}33`,
            }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </AppLayout>
  );
}
